import { PaymentMode } from "types/PaymentMode";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { CustomButton, HrText } from "core/interfaces/components";
import { useCallback, useEffect, useState } from "react";
import IPagamentiProps from "components/GestionePagamenti/IPagamentiProps";
import NavigationManager from "utils/NavigationManager";
import { IPagamento } from "core/interfaces/data/IAssistitoRequest";

import { PagesEnum } from "types/PagesEnum";
import { getCurrentPageName, pagesEnumFromPageName } from "utils/NavigationHelper";
import { personalAreaStep } from "types/StepEnums";
import i18n from "utils/i18n";
import { loadStripe } from "@stripe/stripe-js";
import  PagamentiRep  from "core/data/PagamentiRepository";
import { Elements } from "@stripe/react-stripe-js";

import { Input, Skeleton, Space, Tooltip } from "antd";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";
import { FaCopy } from "react-icons/fa";
import { PayPalScriptOptions } from "@paypal/paypal-js/types/script-options";
import _ from "lodash";
import "./GestionePagamenti.css";
import CustomPaypalButtons from "components/CustomPaypalButtons/CustomPaypalButtons";
import CheckoutForm from "components/CheckoutForm/CheckoutForm";
import { Enhanced } from "utils/ReactUtils";

const GestionePagamenti: React.FC<IPagamentiProps> = ({
	SetPaying,
	PreventivoData,
	OnStrypePaymentDone,
	OnPayPalPaymentDone,
	PaymentModeSelected,
	OnSwitchPaymentMode,
	sessionInfo,
	location,
	t,
	dispatch,
}) => {
	const { GoToPage } = NavigationManager(sessionInfo);

	const [stripeLoading, setStripeLoading] = useState<boolean>(true);
	const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
	const stripePromise = loadStripe(window.CONFIG.REACT_APP_PAYMENT.STRIPE_PK);

	const payPalInitialOptions: PayPalScriptOptions = {
		debug: false,
		"client-id": window.CONFIG.REACT_APP_PAYMENT.PAYPAL_CLIENT_ID,
		currency: "EUR",
		intent: "capture",
		commit: true,
		"disable-funding": `bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort,venmo,card`,
		"data-page-type": "checkout",
	};

	const getCausalePagamento = () => {
		return window.CONFIG.REACT_APP_PAYMENT.CAUSALE.format(PreventivoData?.Nominativo, PreventivoData?.CodPai);
	};

	const getSender = (): PagesEnum => {
		return pagesEnumFromPageName[getCurrentPageName(location.state.from.pathname)];
	};

	const proceedNoPayment = () => {
		if (getSender() === PagesEnum.PersonalArea) {
			GoToPage(PagesEnum.PersonalArea, personalAreaStep.sospesi);
		} else {
			SetPaying(true);
			let payment: IPagamento = {
				Skipped: true,
				Success: false,
				TransactionID: "",
				TipoPagamento: PaymentMode.SaltaPagamento,
			};
			OnPayPalPaymentDone(payment);
		}
	};

	const baseFocus = (controlName: string) => {
		let copyText = document.getElementById(controlName) as HTMLInputElement;
		copyText.select();
		copyText.setSelectionRange(0, 99999); /* For mobile devices */
		return copyText;
	};
	const baseCopy = (controlName: string) => {
		let copyText = baseFocus(controlName);
		navigator.clipboard.writeText(copyText!.value);
	};

	const onIntestatarioCopy = () => {
		baseCopy("intestatario");
	};

	const onIntestatarioFocus = () => {
		baseFocus("intestatario");
	};

	const onBancaCopy = () => {
		baseCopy("banca");
	};

	const onBancaFocus = () => {
		baseFocus("banca");
	};
	const onCausaleCopy = () => {
		baseCopy("causale");
	};

	const onCausaleFocus = () => {
		baseFocus("causale");
	};
	const onIbanCopy = () => {
		baseCopy("iban");
	};

	const onIbanFocus = () => {
		baseFocus("iban");
	};

	const onCopyAll = async () => {
		let disctionaryAtributes = [
			{ text: "Intestatario", controlName: "intestatario" },
			{ text: "Banca", controlName: "banca" },
			{ text: "IBAN", controlName: "iban" },
			{ text: "Causale", controlName: "causale" },
		];
		let res = _.map(disctionaryAtributes, (control) => {
			let prefix = t(`pagamento.text.${control.text}`);
			let copyText = baseFocus(control.controlName);
			return `${prefix}\t${copyText.value}`;
		});

		let finaltext = res.join("\n");
		await navigator.clipboard.writeText(finaltext);
	};

	const createOrder = (data: any, actions: any) => {
		return actions.order
			.create({
				purchase_units: [
					{
						description: getCausalePagamento(),

						amount: {
							currency_code: "EUR",
							value: PreventivoData?.ImportoScontatoConIva! + (PreventivoData?.Bollo ?? 0),
						},
					},
				],
				// not needed if a shipping address is actually needed
				application_context: {
					shipping_preference: "NO_SHIPPING",
				},
			})
			.then((orderID: any) => {
				// setOrderID(orderID);
				return orderID;
			});
	};

	const onApprove = (data: any, actions: any) => {
		let paymentMode = data.paymentSource;
		return actions.order.capture().then(function (details: any) {
			// const { payer } = details;
			// setSuccess(true);
			SetPaying(true);
			let payment: IPagamento = {
				Skipped: false,
				Success: true,
				TransactionID: details.id,
				TipoPagamento: paymentMode === "card" ? PaymentMode.CartaDiCredito : PaymentMode.PayPal,
			};
			OnPayPalPaymentDone(payment);
		});
	};

	const onError = (err: any) => {
		SetPaying(false);
		console.log(err);
	};

	const getClientSecret = useCallback(async () => {
		if (!PreventivoData) return;

		const data = await PagamentiRep.getClientSecret(
			PreventivoData.ImportoScontatoConIva + PreventivoData.Bollo,
			PreventivoData.IdPreventivo!.toString(),
			sessionInfo.nominativoUtente!,
			i18n.language
		);
		if (!data) return;
		setClientSecret(data.ClientSecret);
		setStripeLoading(false);
	}, [PreventivoData, sessionInfo.nominativoUtente]);

	useEffect(() => {
		if (PaymentModeSelected === PaymentMode.CartaDiCredito && !clientSecret) {
			getClientSecret();
		}
	}, [PaymentModeSelected, clientSecret, getClientSecret]);

	return (
		<>
			{PaymentModeSelected === PaymentMode.PayPal && (
				<div className="paypal-payment">
					<HrText text={t("pagamento.text.pagamentoPayPal")} />

					<PayPalScriptProvider options={payPalInitialOptions}>
						<CustomPaypalButtons style={{ layout: "vertical", tagline: false }} createOrder={createOrder} onApprove={onApprove} onError={onError} />
					</PayPalScriptProvider>
					<div className="button-box item">
						<CustomButton text={t("pagamento.backButton")} OnClick={OnSwitchPaymentMode} buttonType={CustomButtonType.negative} />
					</div>
				</div>
			)}
			{PaymentModeSelected === PaymentMode.CartaDiCredito && (
				<div className="stripe-payment">
					<HrText text={t("pagamento.text.pagamentoStripe")} />
					<Skeleton loading={stripeLoading}>
						{clientSecret ? (
							<Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
								<CheckoutForm clientSecret={clientSecret} onSubmit={OnStrypePaymentDone} />
							</Elements>
						) : (
							<>
								<div className="payment-form">
									<span className="payment-error">{t("pagamento.text.errorePagamento")}</span>
								</div>
							</>
						)}
					</Skeleton>
					<div className="button-box item">
						<CustomButton text={t("pagamento.backButton")} OnClick={OnSwitchPaymentMode} buttonType={CustomButtonType.negative} />
					</div>
				</div>
			)}
			{PaymentModeSelected === PaymentMode.Bonifico && (
				<div id={"pagamento-detail"}>
					<HrText text={t("pagamento.text.bonifico")} />
					<span className={"subtitle"}>{t("pagamento.text.pagaSuccessivamenteIstruzioni")}</span>

					<Space.Compact className="input-group intestatario">
						<span className={"text"}>{t("pagamento.text.Intestatario")}</span>
						<div className="payment-content">
							<Input id={"intestatario"} defaultValue={window.CONFIG.REACT_APP_PAYMENT.INTESTATARIO} readOnly={true} onFocus={onIntestatarioFocus} />
							<Tooltip title="Copia Intestatario">
								<CustomButton buttonType={CustomButtonType.positive} text="" icon={<FaCopy />} OnClick={onIntestatarioCopy}></CustomButton>
							</Tooltip>
						</div>
					</Space.Compact>
					<Space.Compact className="input-group banca">
						<span className={"text"}>{t("pagamento.text.Banca")}</span>
						<div className="payment-content">
							<Input id={"banca"} defaultValue={window.CONFIG.REACT_APP_PAYMENT.BANCA} readOnly={true} onFocus={onBancaFocus} />
							<Tooltip title="Copia banca di riferimento">
								<CustomButton buttonType={CustomButtonType.positive} text="" icon={<FaCopy />} OnClick={onBancaCopy} />
							</Tooltip>
						</div>
					</Space.Compact>
					<Space.Compact className="input-group iban">
						<span className={"text"}>{t("pagamento.text.IBAN")}</span>
						<div className="payment-content">
							<Input id={"iban"} defaultValue={window.CONFIG.REACT_APP_PAYMENT.IBAN} readOnly={true} onFocus={onIbanFocus} />
							<Tooltip title="Copia IBAN">
								<CustomButton buttonType={CustomButtonType.positive} text="" icon={<FaCopy />} OnClick={onIbanCopy} />
							</Tooltip>
						</div>
					</Space.Compact>
					<Space.Compact className="input-group causale">
						<span className={"text"}>{t("pagamento.text.Causale")}</span>
						<div className="payment-content">
							<Input id={"causale"} defaultValue={getCausalePagamento()} readOnly={true} onFocus={onCausaleFocus} />
							<Tooltip title="Copia causale Bonifico">
								<CustomButton buttonType={CustomButtonType.positive} text="" icon={<FaCopy />} OnClick={onCausaleCopy} />
							</Tooltip>
						</div>
					</Space.Compact>
					<div className="button-box item copy-all">
						<CustomButton buttonType={CustomButtonType.positive} text={t("pagamento.copiaPerBonifico")} icon={<FaCopy />} OnClick={onCopyAll} />
					</div>
					<br />
					<div className="modalita-pagamento">
						<div className="button-box item">
							<CustomButton text={t("pagamento.backButton")} OnClick={OnSwitchPaymentMode} buttonType={CustomButtonType.negative} />
							<CustomButton
								text={t("pagamento.buttonSubmitBonifico")}
								OnClick={proceedNoPayment}
								buttonType={CustomButtonType.positive}
							></CustomButton>
						</div>
					</div>
					<br />
				</div>
			)}
		</>
	);
};
export default Enhanced( GestionePagamenti,{componentNamespace:"GestionePagamenti"});
